import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Download, TrendingUp, TrendingDown, Minus, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ProductImage } from '@/components/ui/ProductImage'
import { useProductComparison } from '@/hooks/useProductComparison'
import { Skeleton } from '@/components/ui/skeleton'
import { formatCurrency, stripBrandPrefixFromProductName } from '@/lib/utils'
import type { Product } from '@/types'
import { useEffect } from 'react'

export function ProductDetailPage() {
    const { productId } = useParams<{ productId: string }>()
    const location = useLocation()
    const navigate = useNavigate()
    const product = location.state?.product as Product | undefined

    const { loadComparison, selectedProduct, competitors, priceStats, isLoading, clearComparison } = useProductComparison()

    useEffect(() => {
        if (product) {
            loadComparison(product)
        }
        return () => {
            clearComparison()
        }
    }, [productId])

    if (isLoading) {
        return (
            <div className="p-6 md:p-8 max-w-[1400px] mx-auto w-full">
                <Skeleton className="h-8 w-40 mb-6" />
                <Skeleton className="h-10 w-80 mb-4" />
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-5">
                        <Skeleton className="h-40 w-full rounded-xl" />
                    </div>
                    <div className="lg:col-span-7 grid grid-cols-3 gap-4">
                        <Skeleton className="h-32 rounded-xl" />
                        <Skeleton className="h-32 rounded-xl" />
                        <Skeleton className="h-32 rounded-xl" />
                    </div>
                </div>
            </div>
        )
    }

    const displayProduct = selectedProduct?.product || product

    if (!displayProduct) {
        return (
            <div className="p-6 md:p-8 max-w-[1400px] mx-auto w-full text-center">
                <p className="text-muted-foreground">Product not found</p>
                <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
                    Go Back
                </Button>
            </div>
        )
    }

    // Get the user's price from selectedProduct
    const userPrice = selectedProduct?.prices?.[0]?.price || 0
    const displayProductName = stripBrandPrefixFromProductName(displayProduct.name, displayProduct.brand)

    return (
        <div className="p-6 md:p-8 max-w-[1400px] mx-auto w-full flex flex-col gap-6 animate-fade-in">
            {/* Breadcrumb */}
            <nav className="flex items-center text-sm font-medium text-muted-foreground">
                <button onClick={() => navigate('/app/competitor')} className="hover:text-primary transition-colors">
                    Home
                </button>
                <span className="mx-2">/</span>
                <button onClick={() => navigate('/app/competitor')} className="hover:text-primary transition-colors">
                    Competitor Module
                </button>
                <span className="mx-2">/</span>
                <span className="text-foreground">Product Details</span>
            </nav>

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div className="flex flex-col gap-2">
                    <h1 className="text-3xl md:text-4xl font-black tracking-tight text-foreground">
                        {displayProductName}
                    </h1>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span className="bg-muted px-2 py-0.5 rounded text-xs font-mono font-bold text-foreground">
                            SKU: {displayProduct.id}
                        </span>
                        <span>•</span>
                        <span>Brand: {displayProduct.brand}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1 text-accent">
                            <Check className="h-4 w-4" /> Active
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" className="gap-2">
                        <Download className="h-4 w-4" />
                        Export Report
                    </Button>
                </div>
            </div>

            {/* Product Card and Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Product Card */}
                <div className="lg:col-span-5 flex flex-col">
                    <div className="flex-1 flex items-stretch bg-card rounded-xl shadow-soft border border-border overflow-hidden p-5 gap-5">
                        <div className="w-28 h-28 shrink-0 bg-muted rounded-lg overflow-hidden">
                            <ProductImage
                                src={displayProduct.imageUrl}
                                alt={displayProduct.name}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="flex flex-col justify-between flex-1 py-1">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">
                                    Your Listing
                                </p>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-3xl font-bold text-foreground">
                                        {formatCurrency(userPrice)}
                                    </span>
                                    <span className="text-sm font-medium text-muted-foreground">per unit</span>
                                </div>
                                <div className="mt-2 flex items-center gap-2">
                                    <span className="inline-flex items-center px-2 py-1 rounded bg-accent/10 text-xs font-medium text-accent border border-accent/20">
                                        In Stock (450 units)
                                    </span>
                                </div>
                            </div>
                            <Button className="mt-3 w-full h-9 gap-2">
                                Update Price
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="flex flex-col justify-between p-5 rounded-xl bg-card border border-border shadow-soft">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-muted-foreground">Lowest Price</span>
                            <TrendingDown className="h-5 w-5 text-accent" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-foreground">
                                {formatCurrency(priceStats?.lowestPrice || 0)}
                            </p>
                            <p className="text-sm font-medium text-destructive mt-1 flex items-center gap-1">
                                <span className="size-2 rounded-full bg-destructive inline-block" /> {priceStats?.lowestPricePlatform || 'N/A'}
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-col justify-between p-5 rounded-xl bg-card border border-border shadow-soft">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-muted-foreground">Market Avg</span>
                            <Minus className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-foreground">
                                {formatCurrency(priceStats?.competitorAvg || 0)}
                            </p>
                            <p className="text-sm font-medium text-muted-foreground mt-1">
                                Across {priceStats?.totalCompetitors || 0} stores
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-col justify-between p-5 rounded-xl bg-card border border-border shadow-soft">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-muted-foreground">Highest Price</span>
                            <TrendingUp className="h-5 w-5 text-warning" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-foreground">
                                {formatCurrency(priceStats?.highestPrice || 0)}
                            </p>
                            <p className="text-sm font-medium text-accent mt-1 flex items-center gap-1">
                                <span className="size-2 rounded-full bg-accent inline-block" /> {priceStats?.highestPricePlatform || 'N/A'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Competitor Table */}
            <div className="flex flex-col gap-4">
                <h2 className="text-lg font-bold text-foreground px-1">
                    Cross-Product Competitive Analysis
                </h2>
                <div className="bg-card rounded-xl border border-border overflow-hidden shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-muted/50 border-b border-border">
                                    <th className="py-4 px-6 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                        Brand
                                    </th>
                                    <th className="py-4 px-6 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                        Product Name
                                    </th>
                                    <th className="py-4 px-6 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center">
                                        Platform
                                    </th>
                                    <th className="py-4 px-6 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-right">
                                        Price
                                    </th>
                                    <th className="py-4 px-6 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-right">
                                        Discount %
                                    </th>
                                    <th className="py-4 px-6 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-right">
                                        Price Position
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {/* Current product row */}
                                <tr className="bg-primary/5 hover:bg-primary/10 transition-colors">
                                    <td className="py-4 px-6">
                                        <div className="flex items-center gap-3">
                                            <div className="size-10 bg-card rounded-md p-1 border border-border flex items-center justify-center overflow-hidden">
                                                <ProductImage
                                                    src={displayProduct.imageUrl}
                                                    alt={displayProduct.brand}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            <span className="font-bold text-foreground">{displayProduct.brand}</span>
                                        </div>
                                    </td>
                                    <td className="py-4 px-6 font-bold text-foreground">
                                        {displayProductName} (Your Listing)
                                    </td>
                                    <td className="py-4 px-6 text-center text-foreground font-medium">
                                        Blinkit
                                    </td>
                                    <td className="py-4 px-6 text-right font-mono font-bold text-foreground">
                                        {formatCurrency(userPrice)}
                                    </td>
                                    <td className="py-4 px-6 text-right">
                                        <span className="text-muted-foreground font-medium">5%</span>
                                    </td>
                                    <td className="py-4 px-6 text-right">
                                        <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                                            Baseline
                                        </Badge>
                                    </td>
                                </tr>

                                {/* Competitor rows */}
                                {competitors.map((competitor, index) => {
                                    const competitorPrice = competitor.prices?.[0]?.price || 0
                                    const competitorDiscount = competitor.prices?.[0]?.discount || 0
                                    const priceDiff = userPrice - competitorPrice
                                    const status = priceDiff > 0 ? 'undercut' : priceDiff < 0 ? 'premium' : 'competitive'

                                    return (
                                        <tr key={index} className="hover:bg-muted/30 transition-colors">
                                            <td className="py-4 px-6">
                                                <div className="flex items-center gap-3">
                                                    <div className="size-10 bg-muted rounded-md p-1 border border-border flex items-center justify-center">
                                                        <ProductImage
                                                            src={competitor.product.imageUrl}
                                                            alt={competitor.product.brand}
                                                            className="w-full h-full object-contain"
                                                        />
                                                    </div>
                                                    <span className="font-medium text-foreground">{competitor.product.brand}</span>
                                                </div>
                                            </td>
                                            <td className="py-4 px-6 font-medium text-foreground">
                                                {stripBrandPrefixFromProductName(competitor.product.name, competitor.product.brand)}
                                            </td>
                                            <td className="py-4 px-6 text-center text-muted-foreground">
                                                {competitor.prices?.[0]?.platform || 'N/A'}
                                            </td>
                                            <td className="py-4 px-6 text-right font-mono font-medium text-foreground">
                                                {formatCurrency(competitorPrice)}
                                            </td>
                                            <td className="py-4 px-6 text-right">
                                                <span className={status === 'undercut' ? 'text-accent font-medium' : 'text-muted-foreground font-medium'}>
                                                    {competitorDiscount.toFixed(2)}%
                                                </span>
                                            </td>
                                            <td className="py-4 px-6 text-right">
                                                <Badge variant={status}>
                                                    {status === 'undercut' ? 'Undercut' : status === 'premium' ? 'Premium' : 'Match'}
                                                </Badge>
                                            </td>
                                        </tr>
                                    )
                                })}

                                {competitors.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="py-8 text-center text-muted-foreground">
                                            No competitor data available
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Back Button */}
            <div className="mt-4">
                <Button variant="outline" onClick={() => navigate(-1)} className="gap-2">
                    <ArrowLeft className="h-4 w-4" />
                    Back to Search
                </Button>
            </div>
        </div>
    )
}
